/**
 * The advanced (and/or) filter builder — rebuilt in round 5o from the sandbox capture
 * (`discovery/ui-reference/filters/sandbox-advanced-*.png`, `specs/M1-B6-filters-sort.md` §4).
 *
 * Measured chrome, 830 x 281 at (610, 237) with one rule:
 *
 *   Advanced filters   Showing all of 38 items ⓘ        Clear all   [Save as new view]
 *   (•) Filter with AI                                   ← never rendered by us (no AI)
 *   Where   [Column ▾ 170]   [Condition ▾ 135]   [Value ▾ 365]
 *   [And ▾]  [Column ▾]      [Condition ▾]       [Value ▾]
 *   And      [Column ▾]      [Condition ▾]       [Value ▾]
 *   + New filter   + New group
 *   ────────────────────────────────────────────  Switch to quick filters
 *
 * Three things the capture settled:
 *  - the combinator is **per group, edited on the second row only**: row 1 reads a static
 *    `Where`, row 2 is an `And ▾` / `Or ▾` dropdown, rows 3+ repeat the chosen word as plain
 *    text. It is not a per-row toggle.
 *  - `+ New group` appends a **boxed nested panel** with its own `Where` row, its own
 *    `+ New filter` and an `×` that deletes the whole group.
 *  - the Condition dropdown is the column type's `filterConditions` — monday's own wording,
 *    per type (`is` on a status, `=` on a number, `is checked` on a checkbox), including the
 *    rows that repeat one operator with a different `compare_attribute` (`timeline`'s
 *    `starts on` / `ends on`). `formula` and `mirror` **are** offered: B4 §8.5 said they were
 *    not, and the capture says otherwise.
 */
import { findColumnType, type ColumnId, type FilterCondition, type FilterOperator } from '@elizmonday/shared';
import { getColumnUi } from '../../columns/registry';
import { useTable, useTableActions } from '../context';
import { EMPTY_FILTER_GROUP, type FilterGroup, type FilterRule } from '../settings';
import { FilterValueEditor } from './FilterValue';
import { Pane, PANE_BOX } from './Pane';
import { useSaveLabel } from './useSaveLabel';

function replaceAt(group: FilterGroup, path: readonly number[], next: FilterGroup): FilterGroup {
  if (path.length === 0) return next;
  const [head, ...rest] = path as [number, ...number[]];
  return {
    ...group,
    groups: group.groups.map((child, index) => (index === head ? replaceAt(child, rest, next) : child)),
  };
}

function removeAt(group: FilterGroup, path: readonly number[]): FilterGroup {
  const [head, ...rest] = path as [number, ...number[]];
  if (rest.length === 0) {
    return { ...group, groups: group.groups.filter((_, index) => index !== head) };
  }
  return {
    ...group,
    groups: group.groups.map((child, index) => (index === head ? removeAt(child, rest) : child)),
  };
}

function nodeAt(group: FilterGroup, path: readonly number[]): FilterGroup {
  return path.reduce<FilterGroup>((node, index) => node.groups[index] ?? node, group);
}

/** The dropdown row a saved rule came from — operator plus, when it splits, its sub-field. */
function conditionOf(conditions: readonly FilterCondition[], rule: FilterRule): FilterCondition | undefined {
  return (
    conditions.find(
      (entry) =>
        entry.operator === rule.operator && (entry.compareAttribute ?? undefined) === rule.compareAttribute,
    ) ?? conditions.find((entry) => entry.operator === rule.operator)
  );
}

function ruleFor(condition: FilterCondition, columnId: ColumnId): FilterRule {
  return {
    columnId,
    operator: condition.operator,
    compareValue: null,
    ...(condition.compareAttribute === undefined ? {} : { compareAttribute: condition.compareAttribute }),
  };
}

export function AdvancedFilterPane({ onSwitchToQuick }: { onSwitchToQuick(): void }) {
  const payload = useTable((state) => state.payload);
  const effective = useTable((state) => state.effective);
  const facets = useTable((state) => state.facets);
  const dirty = useTable((state) => state.dirty);
  const actions = useTableActions();
  const saveLabel = useSaveLabel();

  const root = effective.filter ?? EMPTY_FILTER_GROUP;
  // A type with no conditions is not listed at all — measured for `button`, `auto_number`
  // and `subtasks`, which monday leaves out of the column picker entirely.
  const columns = (payload?.columns ?? []).filter(
    (column) => (findColumnType(column.type)?.filterConditions.length ?? 0) > 0,
  );

  const update = (path: readonly number[], next: FilterGroup) =>
    actions.setEffective({ filter: replaceAt(root, path, next) });

  const renderGroup = (path: readonly number[], depth: number) => {
    const group = nodeAt(root, path);
    const isRoot = path.length === 0;
    /** Index of the first row in this group, counting rules then nested groups. */
    const leadFor = (index: number) => {
      if (index === 0) return 'where' as const;
      return index === 1 ? ('picker' as const) : ('text' as const);
    };

    return (
      <div
        className="ez-filter-group"
        data-depth={depth}
        data-testid={isRoot ? 'filter-root' : `filter-group-${path.join('-')}`}
        key={path.join('.') || 'root'}
      >
        {group.rules.map((rule, index) => {
          const column = columns.find((entry) => entry.id === rule.columnId);
          const shared = column ? findColumnType(column.type) : undefined;
          const conditions = shared?.filterConditions ?? [];
          const active = conditionOf(conditions, rule);
          const buckets = (facets[rule.columnId] ?? []).map((facet) => ({
            key: facet.value as string | number,
            label: facet.label,
          }));
          const setRule = (patch: Partial<FilterRule>) =>
            update(path, {
              ...group,
              rules: group.rules.map((entry, at) => (at === index ? { ...entry, ...patch } : entry)),
            });

          return (
            <div className="ez-filter-rule" key={index} data-testid={`filter-rule-${index}`}>
              <span className="ez-filter-rule__lead">
                {leadFor(index) === 'where' ? (
                  'Where'
                ) : leadFor(index) === 'picker' ? (
                  <select
                    aria-label="Combinator"
                    data-testid="filter-combinator"
                    value={group.operator}
                    onChange={(event) =>
                      update(path, { ...group, operator: event.target.value as 'and' | 'or' })
                    }
                  >
                    <option value="and">And</option>
                    <option value="or">Or</option>
                  </select>
                ) : (
                  /* Measured: rows 3+ repeat the group's word as plain text, not a control. */
                  <span className="ez-filter-rule__combinator-text">
                    {group.operator === 'or' ? 'Or' : 'And'}
                  </span>
                )}
              </span>

              <select
                aria-label="Column"
                className="ez-filter-rule__column"
                value={rule.columnId}
                onChange={(event) => {
                  const nextColumn = columns.find((entry) => entry.id === event.target.value);
                  const first = findColumnType(nextColumn?.type ?? '')?.filterConditions[0];
                  setRule(
                    first
                      ? ruleFor(first, event.target.value as ColumnId)
                      : { columnId: event.target.value as ColumnId, compareValue: null },
                  );
                }}
              >
                {columns.map((entry) => (
                  <option key={entry.id} value={entry.id}>
                    {entry.title}
                  </option>
                ))}
              </select>

              <select
                aria-label="Condition"
                className="ez-filter-rule__condition"
                value={active?.id ?? ''}
                onChange={(event) => {
                  const next = conditions.find((entry) => entry.id === event.target.value);
                  if (!next) return;
                  setRule({
                    operator: next.operator as FilterOperator,
                    compareAttribute: next.compareAttribute,
                    compareValue: null,
                  });
                }}
              >
                {conditions.map((entry) => (
                  <option key={entry.id} value={entry.id}>
                    {entry.label}
                  </option>
                ))}
              </select>

              <FilterValueEditor
                condition={active}
                column={column}
                buckets={buckets}
                people={payload?.people ?? []}
                value={rule.compareValue}
                onChange={(next) => setRule({ compareValue: next })}
                FilterEditor={column ? getColumnUi(column.type).FilterEditor : undefined}
              />

              <button
                type="button"
                className="ez-filter-rule__remove"
                aria-label="Remove filter"
                onClick={() => update(path, { ...group, rules: group.rules.filter((_, at) => at !== index) })}
              >
                ×
              </button>
            </div>
          );
        })}

        {group.groups.map((_, index) => (
          <div className="ez-filter-group__nested" key={`g${String(index)}`}>
            <span className="ez-filter-rule__lead">
              {group.rules.length + index === 0 ? 'Where' : group.operator === 'or' ? 'Or' : 'And'}
            </span>
            <div className="ez-filter-group__box">
              {renderGroup([...path, index], depth + 1)}
              <button
                type="button"
                className="ez-filter-group__remove"
                aria-label="Remove group"
                onClick={() => actions.setEffective({ filter: removeAt(root, [...path, index]) })}
              >
                ×
              </button>
            </div>
          </div>
        ))}

        <div className="ez-filter-group__actions">
          <button
            type="button"
            className="ez-board-pane__link"
            data-testid={isRoot ? 'new-filter' : `new-filter-${path.join('-')}`}
            onClick={() => {
              const first = columns[0];
              if (!first) return;
              const condition = findColumnType(first.type)?.filterConditions[0];
              if (!condition) return;
              update(path, { ...group, rules: [...group.rules, ruleFor(condition, first.id)] });
            }}
          >
            + New filter
          </button>
          {/* Measured: only the root offers `+ New group`; a nested group offers `+ New filter`. */}
          {isRoot ? (
            <button
              type="button"
              className="ez-board-pane__link"
              data-testid="new-group"
              onClick={() =>
                update(path, {
                  ...group,
                  groups: [...group.groups, { operator: 'or', rules: [], groups: [] }],
                })
              }
            >
              + New group
            </button>
          ) : null}
        </div>
      </div>
    );
  };

  const total = payload?.totals.items ?? 0;
  const filtered = payload?.totals.filtered ?? 0;
  const unfiltered = effective.filter === null && effective.search === '';

  return (
    <Pane
      testId="advanced-filter-pane"
      title="Advanced filters"
      {...PANE_BOX.advancedFilter}
      dirty={dirty}
      canSave={payload?.permissions.canSaveView ?? false}
      saveLabel={saveLabel}
      onSave={() => void actions.saveView()}
      onClear={() => actions.setEffective({ filter: null })}
      clearDisabled={effective.filter === null}
      onClose={() => actions.openPane(null)}
      footer={
        <button type="button" className="ez-board-pane__link" onClick={onSwitchToQuick}>
          Switch to quick filters
        </button>
      }
    >
      {/* Measured wording, both cases: `Showing all of 38 items` / `Showing 6 of 38 items`. */}
      <p className="ez-board-pane__count" data-testid="advanced-filter-count">
        {unfiltered ? `Showing all of ${total} items` : `Showing ${filtered} of ${total} items`}
      </p>
      {renderGroup([], 0)}
    </Pane>
  );
}
